import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['container', 'question', 'input', 'nextButton', 'submitButton', 'navigation', 'errorMessage', 'textWithButtonInput', 'textWithButtonButton', 'textWithButtonNextButton', 'checkboxNextButton', 'citiesSelectedText']
  static values = {
    questions: Array,
    definitionId: Number,
    definitionVersion: Number,
    submitUrl: String,
    displayVisibleClass: String, // 表示用CSSクラス名（ViewComponentでスコープ済み）
    allowSubmission: Boolean, // 開発環境・ステージングでは実際の送信を許可
    gaConfig: Object, // GAイベント設定（gaEvents, submit, categoryId, abCategory）
  }

  connect () {
    this.answers = {} // 内部処理用（value）
    this.answerLabels = {} // extra_input_params用（日本語ラベル）
    this.currentIndex = 0
    this.isSubmitting = false // ダブルサブミット防止フラグ
    this._firstIntroShown = false // first_intro_only: 最初の導入メッセージが表示済みか
    this.isTransitioning = false // 質問遷移中の並行呼び出し防止フラグ
    this._startTime = Date.now() // EF回答開始時刻（経過時間表示用）
    this._timers = [] // setTimeout IDの追跡用（disconnect時にクリア）
    this._abortController = null // Ajax取得のAbortController（disconnect時にキャンセル）
    this._priorityOrders = new Map() // 質問IDごとの優先順位選択順を管理

    // 表示クラス名を取得（最初の質問から取得）
    this.visibleClass = this.displayVisibleClassValue || this.detectVisibleClass()

    if (this.questionTargets.length === 0) {
      console.error('[EF Renderer] No question targets found')
      this.showErrorMessage('フォームの初期化に失敗しました。ページを再読み込みしてください。')
      return
    }

    try {
      this.updateNavigation()
      this.evaluateShowConditions()

      // 初期表示時: すでに表示されているチャット形式の質問のローダーを非表示にして吹き出しを表示
      this.initializeVisibleChatQuestions()
    } catch (e) {
      console.error('[EF Renderer] Failed to initialize:', e)
      if (window.Sentry) window.Sentry.captureException(e)
      this.showErrorMessage('フォームの初期化に失敗しました。ページを再読み込みしてください。')
      return
    }

    // GA追跡の初期化（非クリティカル: 失敗してもフォームは動作する）
    try {
      this.applyGtmDataAttributes()
      this.firePageViewGaEvent()
    } catch (e) {
      console.error('[EF Renderer] Failed to initialize GA tracking:', e)
      if (window.Sentry) window.Sentry.captureException(e)
    }
  }

  disconnect () {
    // 全てのsetTimeoutをクリア（メモリリーク防止）
    this._timers.forEach(id => clearTimeout(id))
    this._timers = []
    // 進行中のAjaxリクエストをキャンセル
    if (this._abortController) {
      this._abortController.abort()
      this._abortController = null
    }
    // モーダルが残っていれば除去
    const modal = document.getElementById('ef-preview-params-modal')
    if (modal) modal.remove()
  }

  // === GAイベント関連 ===

  // GAイベントのプレースホルダーを実際の値で置換して送信する
  // {category_id} → settings.category_id, {ab_category} → definition.name
  fireGaEvent (gaEventConfig) {
    if (!gaEventConfig || typeof window.gtagEvent !== 'function') return

    const config = this.gaConfigValue || {}
    const categoryId = config.categoryId || ''
    const abCategory = config.abCategory || ''

    const action = this.replacePlaceholders(gaEventConfig.action || 'step', categoryId, abCategory)
    const category = this.replacePlaceholders(gaEventConfig.category || '', categoryId, abCategory)
    const label = this.replacePlaceholders(gaEventConfig.label || '', categoryId, abCategory)

    window.gtagEvent(action, category, label)
  }

  // プレースホルダーを置換する（YAML値が整数の場合もあるためString()で型強制）
  replacePlaceholders (template, categoryId, abCategory) {
    return String(template || '')
      .replace(/\{category_id\}/g, String(categoryId))
      .replace(/\{ab_category\}/g, String(abCategory))
  }

  // ページビュー時のGAイベント（ga_events.page_view）
  firePageViewGaEvent () {
    const config = this.gaConfigValue
    if (!config?.gaEvents?.page_view) return

    this.fireGaEvent(config.gaEvents.page_view)
  }

  // 質問回答時のGAイベント（option.ga_event または question.ga_event）
  fireStepGaEvent (questionId, selectedValue) {
    const questionData = this.questionsValue.find(q => q.id === questionId)
    if (!questionData) return

    // checkbox型はselectedValueが配列のため、選択肢レベルのga_eventマッチは行わない
    // 質問レベルのga_eventのみ対応
    if (Array.isArray(selectedValue)) {
      if (questionData.gaEvent) this.fireGaEvent(questionData.gaEvent)
      return
    }

    // 選択肢レベルのga_eventを優先
    if (questionData.options) {
      const selectedOption = questionData.options.find(opt => opt.value === selectedValue)
      if (selectedOption?.gaEvent) {
        this.fireGaEvent(selectedOption.gaEvent)
        return
      }
    }

    // 質問レベルのga_event
    if (questionData.gaEvent) {
      this.fireGaEvent(questionData.gaEvent)
    }
  }

  // 送信時のGAイベント（submit.ga_event）
  fireSubmitGaEvent () {
    const config = this.gaConfigValue
    if (!config?.submit?.ga_event) return

    this.fireGaEvent(config.submit.ga_event)
  }

  // GTM連携用のdata属性をDOM要素にセットする
  // 既存EFと同じ class="inquiry-step-button" data-gtm-event="..." data-gtm-ef-category="..." を付与
  // GTMがクリックイベント時にこれらの属性を読み取ってGAイベントを送信する
  applyGtmDataAttributes () {
    this.questionsValue.forEach(questionData => {
      const questionEl = this.questionTargets.find(q => q.dataset.questionId === questionData.id)
      if (!questionEl) return

      const inputs = questionEl.querySelectorAll('input[type="radio"], input[type="checkbox"]')
      inputs.forEach(input => {
        // 選択肢レベルのga_eventを探す
        const option = questionData.options?.find(opt => opt.value === input.value)
        const gaEvent = option?.gaEvent || questionData.gaEvent
        if (!gaEvent) return

        this.setGtmAttributes(input, this.buildGtmAttributes(gaEvent))
      })
    })
  }

  // ga_event設定からGTM用属性のオブジェクトを構築する
  buildGtmAttributes (gaEvent) {
    if (!gaEvent) return null

    const config = this.gaConfigValue || {}
    const categoryId = config.categoryId || ''
    const abCategory = config.abCategory || ''

    return {
      label: this.replacePlaceholders(gaEvent.label || '', categoryId, abCategory),
      category: this.replacePlaceholders(gaEvent.category || `inqueryCategory:${categoryId}`, categoryId, abCategory),
    }
  }

  // input要素にGTM用data属性をセットする
  setGtmAttributes (inputElement, gtmAttributes) {
    if (!gtmAttributes) return

    inputElement.classList.add('inquiry-step-button')
    inputElement.dataset.gtmEvent = gtmAttributes.label
    inputElement.dataset.gtmEfCategory = gtmAttributes.category
  }

  // setTimeoutをラップし、IDを追跡する（disconnect時に一括クリア）
  _trackTimeout (fn, delay) {
    const id = setTimeout(() => {
      fn()
      // 実行済みのタイマーIDをリストから除去
      this._timers = this._timers.filter(t => t !== id)
    }, delay)
    this._timers.push(id)
    return id
  }

  // 初期表示時にすでに表示されているチャット形式の質問を処理
  // ローダーを非表示にして吹き出しを表示する
  initializeVisibleChatQuestions () {
    const currentQuestion = this.questionTargets[this.currentIndex]
    if (currentQuestion && this.isChatStyleQuestion(currentQuestion)) {
      // 最初のチャット形式質問: ローダーを一定時間表示してから吹き出しに切り替え
      // 本番と同じタイミング: DELAY_TIME * (index + 2) - 400 = 800 * 2 - 400 = 1200ms
      this._trackTimeout(() => {
        this.transitionLoaderToQuestion(currentQuestion)
      }, 1200)
    }
  }

  // 表示用CSSクラスを検出（最初の表示中の質問から取得）
  detectVisibleClass () {
    const firstQuestion = this.questionTargets[0]
    if (!firstQuestion) return ''
    // display-visibleを含むクラスを探す
    const classes = Array.from(firstQuestion.classList)
    const found = classes.find(c => c.includes('display-visible'))
    if (!found) {
      console.warn('[EF Renderer] display-visible class not found on first question, falling back to empty string')
    }
    return found || ''
  }

  // 入力値が変更された時の処理
  onInputChange (event) {
    const questionElement = event.target.closest('[data-question-id]')
    if (!questionElement) return

    const questionId = questionElement.dataset.questionId
    this.saveAnswer(questionId, event.target)

    // 回答が変更されたらボタンの状態を更新
    this.updateNextButtonState()

    // コンポーネント内「次へ進む」ボタンの状態を更新（checkbox/level）
    if (event.target.type === 'checkbox' || event.target.type === 'radio') {
      this.updateCheckboxNextButtonState(questionElement)
    }

    // 複数選択（checkbox）以外は回答後に自動で次へ進む
    // ただし、テキスト入力・テキストエリアは除外（Enter送信時のみ）
    const inputType = event.target.type
    const tagName = event.target.tagName.toLowerCase()

    // 即座に次へ進むべき入力タイプかどうかを判定
    const shouldAutoAdvance = this.shouldAutoAdvanceOnInput(inputType, tagName, questionElement)

    if (shouldAutoAdvance) {
      // 必須の場合はバリデーション通過後のみ自動進行
      const questionData = this.questionsValue.find(q => q.id === questionId)
      if (!questionData?.required || this.canProceedFromQuestion(questionElement)) {
        // 少し遅延させてUI更新が見えるようにする
        this._trackTimeout(() => this.goNext(), 200)
      }
    }
  }

  // コンポーネント内「次へ進む」ボタンの状態を更新（checkbox/level共通）
  updateCheckboxNextButtonState (questionElement) {
    const nextButton = questionElement.querySelector('[data-ef-renderer-target="checkboxNextButton"]')
    if (!nextButton) return

    const checkedCount = questionElement.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').length

    // priority質問の場合はmin_selections以上の選択が必要
    const priorityContainer = questionElement.querySelector('[data-priority-min]')
    const minSelections = priorityContainer ? parseInt(priorityContainer.dataset.priorityMin) || 1 : 1

    this.setButtonEnabled(nextButton, checkedCount >= minSelections)

    // リスト形式の選択済みテキストを更新（本番EFの .cities-selected と同等）
    this.updateCitiesSelectedText(questionElement)
  }

  // リスト形式チェックボックスの選択済みテキストを更新
  // 本番EFでは選択した市区町村名をカンマ区切りで表示する
  updateCitiesSelectedText (questionElement) {
    const selectedTextEl = questionElement.querySelector('[data-ef-renderer-target="citiesSelectedText"]')
    if (!selectedTextEl) return

    const checkedInputs = questionElement.querySelectorAll('input[type="checkbox"]:checked')
    const selectedNames = Array.from(checkedInputs).map(input => {
      const label = input.closest('label')
      const span = label?.querySelector('span')
      return span ? span.textContent : input.value
    })
    selectedTextEl.textContent = selectedNames.join(', ')
  }

  // テキスト入力（input）でEnterキーが押された場合の処理
  onKeyDown (event) {
    // Enterキー以外は無視
    if (event.key !== 'Enter') {
      return
    }

    // textareaはEnterで改行するので除外（専用ハンドラーを使用）
    if (event.target.tagName.toLowerCase() === 'textarea') {
      return
    }

    event.preventDefault()
    this.saveAndProceed(event.target)
  }

  // テキストエリアでCtrl/Cmd+Enterが押された場合の処理
  onTextareaKeyDown (event) {
    // Ctrl+Enter または Cmd+Enter（Mac）の場合のみ次へ進む
    if (event.key !== 'Enter' || (!event.ctrlKey && !event.metaKey)) {
      return
    }

    event.preventDefault()
    this.saveAndProceed(event.target)
  }

  // 入力値を保存し、バリデーション通過時に次の質問へ進む
  // onKeyDownとonTextareaKeyDownの共通処理
  saveAndProceed (inputElement) {
    const questionElement = inputElement.closest('[data-question-id]')
    if (!questionElement) return

    const questionId = questionElement.dataset.questionId
    const questionData = this.questionsValue.find(q => q.id === questionId)

    // 入力値を保存
    this.saveAnswer(questionId, inputElement)

    // 必須の場合はバリデーション後のみ次へ進む
    if (!questionData?.required || this.canProceedFromQuestion(questionElement)) {
      this.goNext()
    }
  }

  // text_with_buttonのテキスト入力変更時の処理
  onTextWithButtonInputChange (event) {
    const questionElement = event.target.closest('[data-question-id]')
    if (!questionElement) return

    const questionId = questionElement.dataset.questionId

    // テキスト入力の値を保存
    this.answers[questionId] = event.target.value
    this.answerLabels[questionId] = event.target.value

    // ボタンの選択をクリア（テキスト入力とボタンは排他的）
    const buttons = questionElement.querySelectorAll('[data-ef-renderer-target="textWithButtonButton"]')
    buttons.forEach(button => {
      button.checked = false
      // ボタンのIDに対応する回答もクリア
      const buttonId = button.dataset.buttonId
      if (buttonId) {
        delete this.answers[buttonId]
        delete this.answerLabels[buttonId]
      }
    })

    // text_with_button内の「次へ進む」ボタンの有効/無効を更新
    this.updateTextWithButtonNextButtonState(questionElement)

    this.updateNextButtonState()
  }

  // text_with_buttonの「次へ進む」ボタンクリック時の処理
  onTextWithButtonNextClick (event) {
    const questionElement = event.target.closest('[data-question-id]')
    if (!questionElement) return

    const questionId = questionElement.dataset.questionId

    // テキスト入力に値がある場合はそれを保存
    const textInput = questionElement.querySelector('[data-ef-renderer-target="textWithButtonInput"]')
    if (textInput && textInput.value.trim()) {
      this.answers[questionId] = textInput.value
      this.answerLabels[questionId] = textInput.value
      this.goNext()
      return
    }

    // ボタンが選択されている場合はそのまま次へ進む（回答はボタンクリック時に保存済み）
    const selectedButton = questionElement.querySelector('[data-ef-renderer-target="textWithButtonButton"]:checked')
    if (selectedButton) {
      this.goNext()
    }
  }

  // text_with_button内の「次へ進む」ボタンの状態を更新
  updateTextWithButtonNextButtonState (questionElement) {
    const textInput = questionElement.querySelector('[data-ef-renderer-target="textWithButtonInput"]')
    const nextButton = questionElement.querySelector('[data-ef-renderer-target="textWithButtonNextButton"]')

    if (!nextButton) return

    // テキスト入力に値があるか、ボタンが選択されていれば有効化
    const hasTextValue = textInput && textInput.value.trim()
    const hasButtonSelected = questionElement.querySelector('[data-ef-renderer-target="textWithButtonButton"]:checked')
    this.setButtonEnabled(nextButton, !!(hasTextValue || hasButtonSelected))
  }

  // text_with_buttonのボタンクリック時の処理
  onTextWithButtonButtonClick (event) {
    const button = event.target
    const questionElement = button.closest('[data-question-id]')
    if (!questionElement) return

    const textInputId = button.dataset.textInputId
    const buttonId = button.dataset.buttonId
    const buttonValue = button.value

    // ボタンのラベルを取得
    const label = button.closest('label')
    const buttonLabel = label ? label.querySelector('span')?.textContent || buttonValue : buttonValue

    // ボタンの回答を保存（ボタンは独自のIDを持つ）
    this.answers[buttonId] = buttonValue
    this.answerLabels[buttonId] = buttonLabel

    // テキスト入力の値をクリア（テキスト入力とボタンは排他的）
    if (textInputId) {
      const textInput = questionElement.querySelector('[data-ef-renderer-target="textWithButtonInput"]')
      if (textInput) {
        textInput.value = ''
      }
      delete this.answers[textInputId]
      delete this.answerLabels[textInputId]
    }

    // ボタン選択時は「次へ進む」ボタンを有効化（自動遷移はしない）
    this.updateTextWithButtonNextButtonState(questionElement)
  }

  // 優先順位選択のチェックボックス変更時の処理
  onPriorityChange (event) {
    const checkbox = event.target
    const container = checkbox.closest('[data-priority-max]')
    if (!container) return

    const maxSelections = parseInt(container.dataset.priorityMax) || 3

    // 選択順を管理する配列（Controllerインスタンスで管理）
    const questionElement = container.closest('[data-ef-renderer-target="question"]')
    const questionId = questionElement?.dataset?.questionId || 'unknown'
    if (!this._priorityOrders.has(questionId)) {
      this._priorityOrders.set(questionId, [])
    }
    const priorityOrder = this._priorityOrders.get(questionId)

    if (checkbox.checked) {
      // 選択数制限のチェック
      if (priorityOrder.length >= maxSelections) {
        checkbox.checked = false
        return
      }
      // 選択順に追加
      priorityOrder.push(checkbox.value)
    } else {
      // 選択解除時は配列から削除
      const index = priorityOrder.indexOf(checkbox.value)
      if (index > -1) {
        priorityOrder.splice(index, 1)
      }
    }

    // 番号バッジの更新
    this.updatePriorityBadges(container, priorityOrder)

    // 回答を保存（選択順で保存）
    // 旧EFと同じ形式: 「N位ラベル」でvalueとlabelの両方に順位を含める
    if (questionElement) {
      // valueも「N位{元のvalue}」形式にする（旧EFと同じ）
      this.answers[questionId] = priorityOrder.map((value, index) => {
        const rank = index + 1
        return `${rank}位${value}`
      })
      // ラベルも「(N位)ラベル」形式にする（既存EFと同じ）
      this.answerLabels[questionId] = priorityOrder.map((value, index) => {
        const rank = index + 1
        const input = container.querySelector(`input[value="${CSS.escape(value)}"]`)
        const label = input ? this.getLabelForInput(input) : value
        return `(${rank}位)${label}`
      })
    }

    this.updateNextButtonState()

    // コンポーネント内の「次へ進む」ボタンの有効/無効を更新
    if (questionElement) {
      this.updateCheckboxNextButtonState(questionElement)
    }
  }

  // 優先順位バッジを更新
  updatePriorityBadges (container, priorityOrder) {
    const checkboxes = container.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(cb => {
      const label = cb.closest('label')
      const badge = label?.querySelector('[data-priority-rank-badge]')
      if (badge) {
        const rank = priorityOrder.indexOf(cb.value)
        badge.textContent = rank > -1 ? (rank + 1).toString() : ''
      }
    })
  }

  // 優先順位選択肢の「回答したもの」テキストを更新
  // 各選択肢に data-priority-answer-text="questionId" があり、
  // そのquestionIdに対応する過去の回答を表示する
  updatePriorityAnswerTexts (questionElement) {
    const answerTextElements = questionElement.querySelectorAll('[data-priority-answer-text]')
    answerTextElements.forEach(element => {
      const dependsOnQuestionIds = element.dataset.priorityAnswerText
      if (dependsOnQuestionIds) {
        // カンマ区切りで複数のquestionIdに対応（例: "prefecture_id,city_id"）
        const ids = dependsOnQuestionIds.split(',')
        const answerLabel = ids.map(id => this.answerLabels[id.trim()] || this.answers[id.trim()] || '').filter(Boolean).join('')
        element.textContent = answerLabel
      }
    })
  }

  // 入力後に自動で次へ進むべきかどうかを判定
  shouldAutoAdvanceOnInput (inputType, tagName, questionElement) {
    // checkbox（複数選択）は自動進行しない（「次へ」ボタンで進む）
    if (inputType === 'checkbox') {
      return false
    }

    // テキスト入力・テキストエリアは自動進行しない
    if (inputType === 'text' || inputType === 'tel' || inputType === 'email' || tagName === 'textarea') {
      return false
    }

    // select要素は、必須の場合は値が選択されたら自動進行
    if (tagName === 'select') {
      const selectElement = questionElement.querySelector('select')
      return selectElement && selectElement.value !== ''
    }

    // radio（単一選択）は自動で次へ進む
    // ただし、levelタイプとconfirm_button付きは「次へ進む」ボタンで進むため除外
    if (inputType === 'radio') {
      const questionId = questionElement.dataset.questionId
      const questionData = this.questionsValue.find(q => q.id === questionId)
      if (questionData?.type === 'level') {
        return false
      }
      if (questionData?.confirmButton) {
        return false
      }
      return true
    }

    return false
  }

  // 回答を保存（valueとlabel両方）
  saveAnswer (questionId, inputElement) {
    if (inputElement.type === 'checkbox') {
      // チェックボックスは配列で管理
      const checkboxes = inputElement.closest('[data-question-id]').querySelectorAll('input[type="checkbox"]:checked')
      this.answers[questionId] = Array.from(checkboxes).map(cb => cb.value)
      // ラベルも保存（YAML定義のanswerLabelがあればそちらを優先）
      this.answerLabels[questionId] = Array.from(checkboxes).map(cb => this.getAnswerLabel(questionId, cb.value) || this.getLabelForInput(cb))
    } else if (inputElement.type === 'radio') {
      this.answers[questionId] = inputElement.value
      // ラベルも保存（YAML定義のanswerLabelがあればそちらを優先）
      this.answerLabels[questionId] = this.getAnswerLabel(questionId, inputElement.value) || this.getLabelForInput(inputElement)
    } else if (inputElement.tagName.toLowerCase() === 'select') {
      // select要素は選択されたoptionのテキストをラベルとして保存
      this.answers[questionId] = inputElement.value
      const selectedOption = inputElement.options[inputElement.selectedIndex]
      this.answerLabels[questionId] = this.getAnswerLabel(questionId, inputElement.value) || (selectedOption ? selectedOption.text : inputElement.value)
    } else {
      // テキスト入力はvalueとlabelが同じ
      this.answers[questionId] = inputElement.value
      this.answerLabels[questionId] = inputElement.value
    }
  }

  // YAML定義のoption.answerLabelを取得（未定義ならnull、空文字列は空文字列として返す）
  getAnswerLabel (questionId, value) {
    const questionData = this.questionsValue.find(q => q.id === questionId)
    if (!questionData?.options) return null
    const option = questionData.options.find(o => o.value === value)
    return option?.answerLabel ?? null
  }

  // 入力要素に対応するラベルテキストを取得
  getLabelForInput (inputElement) {
    const label = inputElement.closest('label')
    if (label) {
      const span = label.querySelector('span')
      if (span) {
        // priority型: span内に「回答したもの：」等の子spanが含まれるため、
        // 最初のテキストノードのみ取得（子要素のテキストを除外）
        const firstTextNode = Array.from(span.childNodes).find(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim())
        return firstTextNode ? firstTextNode.textContent.trim() : span.textContent.trim()
      }
      return label.textContent.trim()
    }
    return inputElement.value
  }

  // 次の質問へ進む
  goNext () {
    if (this.isTransitioning) return
    this.isTransitioning = true

    const currentQuestion = this.questionTargets[this.currentIndex]
    if (!this.validateQuestion(currentQuestion)) {
      this.isTransitioning = false
      return
    }

    // 回答時のGAイベントを送信（非クリティカル: 失敗してもフォーム遷移は継続する）
    const questionId = currentQuestion.dataset.questionId
    try {
      this.fireStepGaEvent(questionId, this.answers[questionId])
    } catch (e) {
      console.error('[EF Renderer] Failed to fire step GA event:', e)
      if (window.Sentry) window.Sentry.captureException(e)
    }

    // 次の質問を決定
    const nextIndex = this.determineNextQuestion(currentQuestion)
    if (nextIndex === null) {
      // 最後の質問だった場合
      this.isTransitioning = false
      return
    }

    try {
      this.showQuestion(nextIndex)
    } finally {
      // showQuestion内のアニメーション完了前に次の遷移が始まるのを防ぐ
      this._trackTimeout(() => {
        this.isTransitioning = false
      }, 400)
    }
  }

  // 次の質問のインデックスを決定
  determineNextQuestion (currentQuestion) {
    const questionId = currentQuestion.dataset.questionId
    const questionData = this.questionsValue.find(q => q.id === questionId)

    // questionDataが見つからない場合は次の質問を探す
    if (!questionData) {
      console.error('[EF Renderer] Question data not found', { questionId })
      return this.findNextVisibleQuestion(this.currentIndex)
    }

    // 選択肢ごとの分岐を確認
    if (questionData.options && questionData.options.length > 0) {
      const selectedValue = this.answers[questionId]
      const selectedOption = questionData.options.find(opt => opt.value === selectedValue)
      if (selectedOption && selectedOption.next) {
        return this.findQuestionIndex(selectedOption.next)
      }
    }

    // 質問レベルの分岐を確認
    if (questionData.next) {
      return this.findQuestionIndex(questionData.next)
    }

    // 次の表示可能な質問を探す
    return this.findNextVisibleQuestion(this.currentIndex)
  }

  // 質問IDからインデックスを検索
  findQuestionIndex (questionId) {
    const index = this.questionTargets.findIndex(q => q.dataset.questionId === questionId)
    if (index === -1) {
      console.error('[EF Renderer] 遷移先の質問が見つかりません', { questionId })
      return null
    }
    return index
  }

  // 次の表示可能な質問を探す
  findNextVisibleQuestion (fromIndex) {
    for (let i = fromIndex + 1; i < this.questionTargets.length; i++) {
      if (this.shouldShowQuestion(this.questionTargets[i])) {
        return i
      }
    }
    return null
  }

  // 残りの表示可能な質問数をカウント（message型とlevel型は除外）
  countRemainingVisibleQuestions (fromIndex, includeCurrent = false) {
    let count = 0
    const startIndex = includeCurrent ? fromIndex : fromIndex + 1
    for (let i = startIndex; i < this.questionTargets.length; i++) {
      if (this.shouldShowQuestion(this.questionTargets[i])) {
        const questionId = this.questionTargets[i].dataset.questionId
        const questionData = this.questionsValue.find(q => q.id === questionId)
        // message型（自動遷移）とlevel型（こだわり条件の追加設問）はカウントしない
        if (questionData && questionData.type !== 'message' && questionData.type !== 'level') {
          count++
        }
      }
    }
    return count
  }

  // show_if条件を評価
  shouldShowQuestion (questionElement) {
    const showIfData = questionElement.dataset.showIf
    if (!showIfData) return true

    try {
      const condition = JSON.parse(showIfData)

      // and: 複数条件のAND評価（すべての条件を満たす場合のみ表示）
      if (condition.and) {
        return condition.and.every(subCondition => this.evaluateCondition(subCondition, questionElement))
      }

      return this.evaluateCondition(condition, questionElement)
    } catch (e) {
      console.error('[EF Renderer] Failed to parse show_if condition', { showIfData, error: e.message })
      if (window.Sentry) window.Sentry.captureException(e)
      // パースエラー時は質問を表示する（非表示にするとフォーム完了不能になるリスクがある）
      return true
    }
  }

  // 単一のshow_if条件を評価
  evaluateCondition (condition, questionElement) {
    const answerValue = this.answers[condition.question]

    // value_any_of: 回答（配列）の中にいずれかの値が含まれるか
    if (condition.value_any_of) {
      if (!Array.isArray(answerValue)) return false
      const matched = answerValue.filter(v => condition.value_any_of.includes(v))
      if (matched.length > 0) {
        // 動的テキスト差し替え: {{question_id}} を一致した値で置換
        this.updateDynamicText(questionElement, condition.question, matched)
      }
      return matched.length > 0
    }

    // values: 複数の許容値（ラジオボタン等の単一回答に対して複数値を許容）
    if (condition.values) {
      if (Array.isArray(answerValue)) {
        return answerValue.some(v => condition.values.includes(v))
      }
      return condition.values.includes(answerValue)
    }

    if (Array.isArray(answerValue)) {
      return answerValue.includes(condition.value)
    }
    return answerValue === condition.value
  }

  // 質問テキスト内の {{question_id}} プレースホルダーを実際の回答値で置換
  updateDynamicText (questionElement, questionId, matchedValues) {
    const placeholder = `{{${questionId}}}`
    const replacement = matchedValues.join('、')

    // 吹き出し内テキスト
    const chatText = questionElement.querySelector('[data-ef-chat-question]')
    if (chatText && chatText.dataset.originalText === undefined) {
      chatText.dataset.originalText = chatText.textContent
    }
    if (chatText && chatText.dataset.originalText) {
      chatText.textContent = chatText.dataset.originalText.replace(placeholder, replacement)
    }

    // ヘッダー形式テキスト
    const headingText = questionElement.querySelector('[class*="question-heading"]')
    if (headingText && headingText.dataset.originalText === undefined) {
      headingText.dataset.originalText = headingText.textContent
    }
    if (headingText && headingText.dataset.originalText) {
      headingText.textContent = headingText.dataset.originalText.replace(placeholder, replacement)
    }
  }

  // すべての質問のshow_if条件を評価
  evaluateShowConditions () {
    this.questionTargets.forEach((question, index) => {
      if (index === this.currentIndex) return // 現在の質問はスキップ

      const shouldShow = this.shouldShowQuestion(question)
      if (!shouldShow) {
        // 非表示にする（クラスを削除）
        if (this.visibleClass) {
          question.classList.remove(this.visibleClass)
        } else {
          question.style.display = 'none'
        }
      }
    })
  }

  // 質問がチャット形式かどうかを判定
  isChatStyleQuestion (questionElement) {
    // .chat-question-container が含まれているかで判定
    return questionElement.querySelector('[class*="chat-question-container"]') !== null
  }

  // 選択された回答のテキストを取得
  getSelectedAnswerText (questionElement) {
    const questionId = questionElement.dataset.questionId
    const answer = this.answers[questionId]
    if (!answer) {
      // text_with_button: ボタン選択時は回答がボタンのIDで保存されているため、
      // 選択されたボタンのラベルを取得する
      const selectedButton = questionElement.querySelector('[data-ef-renderer-target="textWithButtonButton"]:checked')
      if (selectedButton) {
        const buttonId = selectedButton.dataset.buttonId
        const label = this.answerLabels[buttonId] || selectedButton.value || ''
        return label ? label + 'です' : ''
      }
      // テキスト入力で未入力の場合
      const questionData = this.questionsValue.find(q => q.id === questionId)
      if (questionData && ['text', 'textarea'].includes(questionData.type)) {
        return '未入力です'
      }
      return ''
    }

    // checkbox/priority（配列）の場合は複数の回答を連結
    if (Array.isArray(answer)) {
      const questionData = this.questionsValue.find(q => q.id === questionId)

      // YAML定義のanswerSeparator/answerSuffixで区切り・末尾テキストを制御
      const separator = questionData?.answerSeparator === 'br' ? '<br>' : '、'
      const suffix = questionData?.answerSuffix != null ? questionData.answerSuffix : 'です'

      // priority型: answerLabelsに「(N位)ラベル」形式で保存済みなのでそちらを使用
      const storedLabels = this.answerLabels[questionId]
      if (questionData?.type === 'priority' && Array.isArray(storedLabels)) {
        return storedLabels.join(separator) + suffix
      }

      const labels = answer.map(value => {
        // YAML定義のanswerLabelを優先
        const yamlLabel = this.getAnswerLabel(questionId, value)
        if (yamlLabel) return yamlLabel
        const input = questionElement.querySelector(`input[value="${CSS.escape(value)}"]`)
        if (input) {
          const label = input.closest('label')
          if (label) {
            const span = label.querySelector('span')
            return span ? span.textContent : label.textContent
          }
        }
        return value
      })
      return labels.join(separator) + suffix
    }

    // YAML定義のanswerLabelを優先（answer_labelには既にsuffixが含まれている）
    const yamlLabel = this.getAnswerLabel(questionId, answer)
    if (yamlLabel) return yamlLabel

    // answerSuffixがある場合はDOMラベルに付加（Ajax選択肢等、answer_labelがない設問用）
    const questionData2 = this.questionsValue.find(q => q.id === questionId)
    const singleSuffix = questionData2?.answerSuffix != null ? questionData2.answerSuffix : ''

    // ラジオボタンの選択されたラベルテキストを取得
    const selectedInput = questionElement.querySelector(`input[value="${CSS.escape(answer)}"]`)
    if (selectedInput) {
      const label = selectedInput.closest('label')
      if (label) {
        const span = label.querySelector('span')
        const text = span ? span.textContent : label.textContent
        return text + singleSuffix
      }
    }
    // セレクトボックスの場合
    const select = questionElement.querySelector('select')
    if (select) {
      const selectedOption = select.options[select.selectedIndex]
      const text = selectedOption ? selectedOption.textContent : answer
      return text + singleSuffix
    }
    return answer + singleSuffix
  }

  // チャット形式の回答吹き出しを挿入
  insertChatAnswerBubble (questionElement, answerText) {
    // 既存の回答吹き出しがあれば削除（重複防止）
    const existingAnswer = questionElement.querySelector('[class*="chat-answer-container"]')
    if (existingAnswer) {
      existingAnswer.remove()
    }

    // チャット入力エリアを取得
    const chatInput = questionElement.querySelector('[class*="chat-input"]')
    if (!chatInput) return

    // CSSクラス名のプレフィックスを取得（ViewComponentのスコープ）
    const className = this.getChatClassName()

    // 回答吹き出しをDOM APIで作成（XSS防止のためinsertAdjacentHTMLは使わない）
    const answerContainer = document.createElement('div')
    answerContainer.className = `${className}chat-answer-container`
    const readDiv = document.createElement('div')
    readDiv.className = `${className}chat-answer-read`
    readDiv.textContent = '既読'
    const answerDiv = document.createElement('div')
    answerDiv.className = `${className}chat-answer`
    // \nと<br>を含むラベルの改行をサポート（XSS防止のためtextContentで各行を設定）
    const answerLines = answerText.split(/\n|<br>/i)
    answerLines.forEach((line, i) => {
      if (i > 0) answerDiv.appendChild(document.createElement('br'))
      answerDiv.appendChild(document.createTextNode(line))
    })
    answerContainer.appendChild(readDiv)
    answerContainer.appendChild(answerDiv)

    // 即座に選択肢を見えなくし、300ms後に回答吹き出しを挿入
    // CSSアニメーション(forwards)はインラインstyleより優先されるためアニメーションをクリア
    chatInput.style.animation = 'none'
    chatInput.style.visibility = 'hidden'
    chatInput.style.opacity = '0'

    // .chatコンテナの高さを固定し、内部のDOM操作による高さ変化を防止
    // NOTE: closest('[class*="::chat"]')はchatInput自身にマッチするため、parentElementから探す
    const chatContainer = questionElement.querySelector(':scope > [class$="\\:\\:chat"]')
    if (chatContainer) {
      chatContainer.style.height = chatContainer.offsetHeight + 'px'
      chatContainer.style.overflow = 'hidden'
    }

    this._trackTimeout(() => {
      // 高さ固定を解除（parentNodeの有無にかかわらず実行）
      if (chatContainer && chatContainer.isConnected) {
        chatContainer.style.height = ''
        chatContainer.style.overflow = ''
      }
      const parent = chatInput.parentNode
      if (!parent) return
      parent.insertBefore(answerContainer, chatInput)
      chatInput.style.display = 'none'
    }, 300)
  }

  // ViewComponentのCSSクラス名プレフィックスを取得
  // 本番の形式: "viewcomponent::inquiry::chat::chat-input" → プレフィックス "viewcomponent::inquiry::chat::"
  getChatClassName () {
    // キャッシュがあれば返す
    if (this._chatClassPrefix !== undefined) {
      return this._chatClassPrefix
    }

    // 既存のクラスからプレフィックスを抽出
    const chatInput = this.element.querySelector('[class*="chat-input"]')
    if (chatInput) {
      const classes = Array.from(chatInput.classList)
      for (const cls of classes) {
        if (cls.endsWith('chat-input')) {
          // "viewcomponent::inquiry::chat::chat-input" → "viewcomponent::inquiry::chat::"
          this._chatClassPrefix = cls.replace('chat-input', '')
          return this._chatClassPrefix
        }
      }
    }
    console.warn('[EF Renderer] chat-input class not found, falling back to empty prefix')
    this._chatClassPrefix = ''
    return this._chatClassPrefix
  }

  // display-hiddenクラス名を取得
  getDisplayHiddenClass () {
    const className = this.getChatClassName()
    return `${className}display-hidden`
  }

  // visibility-visibleクラス名を取得
  getVisibilityVisibleClass () {
    const className = this.getChatClassName()
    return `${className}visibility-visible`
  }

  // 質問を表示
  showQuestion (index) {
    if (index < 0 || index >= this.questionTargets.length) return

    const currentQ = this.questionTargets[this.currentIndex]
    const nextQ = this.questionTargets[index]

    // チャット形式かどうかで処理を分岐
    if (this.isChatStyleQuestion(currentQ)) {
      // チャット形式: 質問は表示したまま、入力エリアを非表示にして回答吹き出しを表示
      const answerText = this.getSelectedAnswerText(currentQ)
      this.insertChatAnswerBubble(currentQ, answerText)
      // 質問自体は表示したまま（visibleClassを削除しない）

      // level型質問: 次もlevel型の場合、前の質問のメイン吹き出しを非表示にする
      // 「エリアや予算によっては〜」テキストはチャット履歴に残り続けるため、
      // 次のlevel質問表示時に前の質問のメイン吹き出しを隠す
      const currentQuestionId = currentQ.dataset.questionId
      const currentQuestionData = this.questionsValue.find(q => q.id === currentQuestionId)
      const nextQuestionId = nextQ.dataset.questionId
      const nextQuestionData = this.questionsValue.find(q => q.id === nextQuestionId)
      // level→level遷移時: 前の質問の吹き出し・sub_messages・回答をすべて残す
      // （導入メッセージ、質問テキスト、回答吹き出しがチャット履歴として表示される）
    } else {
      // ヘッダー形式: 従来通り質問を非表示
      if (this.visibleClass) {
        currentQ.classList.remove(this.visibleClass)
      } else {
        currentQ.style.display = 'none'
      }
    }

    // 次の質問のquestion-containerを表示し、共通セットアップを実行
    this.currentIndex = index
    if (this.isChatStyleQuestion(currentQ)) {
      // 既存EFと同じ方式: 回答直後に次のquestion-containerをdisplay:blockにする
      // 中身（吹き出し・選択肢）はまだ非表示だが、コンテナの高さが確保されることで
      // 選択肢→回答吹き出しの高さ変化によるスクロールのカクつきを軽減する
      if (this.visibleClass) {
        nextQ.classList.add(this.visibleClass)
      } else {
        nextQ.style.display = ''
      }
      // 回答吹き出し挿入（300ms）完了後に内部のセットアップを実行
      this._trackTimeout(() => {
        this.setupNextQuestion(nextQ, { scrollOnChat: true })
      }, 400)
    } else {
      this.setupNextQuestion(nextQ, { scrollOnChat: false })
    }
  }

  // 次の質問を同一画面内に連続表示（auto_continue用）
  // 現在の質問は表示したまま、次の質問を追加で表示
  showNextQuestionInline () {
    const currentQuestion = this.questionTargets[this.currentIndex]

    // 次の質問を決定
    const nextIndex = this.determineNextQuestion(currentQuestion)
    if (nextIndex === null) {
      return
    }

    this.currentIndex = nextIndex
    const nextQ = this.questionTargets[nextIndex]
    this.setupNextQuestion(nextQ, { scrollOnChat: true })
  }

  // 次の質問表示後の共通セットアップ処理
  // showQuestionとshowNextQuestionInlineの両方から呼ばれる
  setupNextQuestion (nextQ, { scrollOnChat = false } = {}) {
    // 質問要素を表示
    if (this.visibleClass) {
      nextQ.classList.add(this.visibleClass)
    } else {
      nextQ.style.display = ''
    }

    // 地方→都道府県→市区町村の連動フィルタリング
    this.filterOptionsBasedOnPreviousAnswer(nextQ)

    // ナビゲーション更新
    this.updateNavigation()

    // show_if条件を再評価
    this.evaluateShowConditions()

    // ルート別の動的テキスト変更（本番EFと同じ挙動を再現）
    this.applyRouteSpecificText(nextQ)

    // 設問テキスト内の {{question_id}} プレースホルダーを過去の回答ラベルで置換
    this.applyAnswerPlaceholders(nextQ)

    // 優先順位選択肢の「回答したもの」テキストを更新
    this.updatePriorityAnswerTexts(nextQ)

    // チャット形式の場合: 吹き出しコンテナの表示→ローダー→吹き出しへの切り替えアニメーション
    if (this.isChatStyleQuestion(nextQ)) {
      const chatQuestionContainer = nextQ.querySelector('[data-ef-chat-question-container]')
      const nextQuestionId2 = nextQ.dataset.questionId
      const nextQData = this.questionsValue.find(q => q.id === nextQuestionId2)
      const isSkippedIntro = nextQData?.firstIntroOnly && this._firstIntroShown

      if (isSkippedIntro && chatQuestionContainer) {
        // first_intro_only 2問目以降: コンテナの中身を非表示にし、
        // コンテナをposition:absoluteにしてアイコンだけ浮かせる
        // → sub_messageの横にアイコンが表示され、コンテナはスペースを取らない
        const bubble = nextQ.querySelector('[data-ef-bubble]')
        if (bubble) bubble.style.display = 'none'
        const prefix = this.getChatClassName()
        const adviserName = chatQuestionContainer.querySelector(`.${CSS.escape(`${prefix}adviser-name`)}`)
        if (adviserName) adviserName.style.display = 'none'
        chatQuestionContainer.style.position = 'absolute'
        chatQuestionContainer.style.padding = '0'
        chatQuestionContainer.style.minHeight = '0'
        chatQuestionContainer.style.width = '52px'
        chatQuestionContainer.style.height = '46px'
        chatQuestionContainer.style.backgroundPosition = 'top left'
        // コンテナをvisibleにしてアイコンを表示 + スクロール（既存EFと同様）
        this._trackTimeout(() => {
          window.scroll({ top: nextQ.offsetTop, left: 0, behavior: 'smooth' })
          chatQuestionContainer.classList.add(this.getVisibilityVisibleClass())
        }, 400)
        // ローダー不要なのでtransitionを直接呼ぶ
        this._trackTimeout(() => {
          this.transitionLoaderToQuestion(nextQ)
        }, 800)
      } else {
        // 通常: 吹き出しコンテナをvisibleにしてローダーを表示 + スクロール（既存EFと同様）
        if (chatQuestionContainer) {
          this._trackTimeout(() => {
            window.scroll({ top: nextQ.offsetTop, left: 0, behavior: 'smooth' })
            chatQuestionContainer.classList.add(this.getVisibilityVisibleClass())
          }, 400)
        }
        // ローダー表示後、吹き出しに切り替え
        this._trackTimeout(() => {
          this.transitionLoaderToQuestion(nextQ)
        }, 1200)
      }
    } else {
      // hide_bubble質問（tel等）: 吹き出しがないため直接入力エリアを表示
      const chatInput = nextQ.querySelector('[data-ef-chat-input]')
      if (chatInput) {
        this.fadeInElement(chatInput)
      }
      // 非チャット形式の場合のみ、setupNextQuestion側でスクロール
      if (scrollOnChat) {
        this._trackTimeout(() => {
          window.scroll({ top: nextQ.offsetTop, left: 0, behavior: 'smooth' })
        }, 400)
      }
    }

    // auto_continue属性がある場合は次の質問を同一画面内に連続表示
    const hasAutoContinue = nextQ.dataset.autoContinue === 'true'
    if (hasAutoContinue) {
      // sub_messagesがある場合は全表示完了後まで待つ（既存EFに合わせる）
      const subMsgCount = nextQ.querySelectorAll('[data-ef-sub-message]').length
      // 既存EF: DELAY_TIME(1000) * 6 = 6000ms で次の質問コンテナ表示
      // sub_messagesの表示完了時間 + 余裕を加算
      const autoContinueDelay = subMsgCount > 0
        ? 1200 + 1000 + subMsgCount * 1500 + 1500
        : 2400
      this._trackTimeout(() => {
        this.showNextQuestionInline()
      }, autoContinueDelay)
      return
    }

    // message型質問は自動的に次の質問へ進む（入力がないため）
    const nextQuestionId = nextQ.dataset.questionId
    const nextQuestionData = this.questionsValue.find(q => q.id === nextQuestionId)
    if (nextQuestionData?.type === 'message') {
      // sub_messagesがある場合は全表示完了後まで待つ
      const subMsgCount = nextQ.querySelectorAll('[data-ef-sub-message]').length
      const messageDelay = subMsgCount > 0
        ? 1200 + 1000 + subMsgCount * 1500 + 1500
        : 3600
      this._trackTimeout(() => {
        this.goNext()
      }, messageDelay)
    }
  }

  // ローダーを非表示にして質問テキストを表示
  // 吹き出しの外枠は共通で、中身（ローダー or テキスト）だけを切り替える
  // これにより位置の移動が発生しない
  transitionLoaderToQuestion (questionElement) {
    const loader = questionElement.querySelector('[data-ef-loader]')
    const chatQuestion = questionElement.querySelector('[data-ef-chat-question]')
    const annotation = questionElement.querySelector('[data-ef-annotation]')
    const chatInput = questionElement.querySelector('[data-ef-chat-input]')
    const subMessages = questionElement.querySelectorAll('[data-ef-sub-message]')

    // ローダーを非表示（ef-bubble-hiddenクラスを追加）
    if (loader) {
      loader.classList.add('ef-bubble-hidden')
    }

    // 質問テキストを表示（ef-bubble-hiddenクラスを削除）
    const questionId = questionElement.dataset.questionId

    // first_intro_only: 同グループ内で最初に表示される質問のみ吹き出し（text）を表示
    // 2回目以降はtext（導入メッセージ）を非表示にし、sub_messages（質問テキスト）のみ表示
    const questionData2 = this.questionsValue.find(q => q.id === questionId)
    if (questionData2?.firstIntroOnly) {
      if (this._firstIntroShown) {
        // 2回目以降: 導入文バブルのみ非表示（setupNextQuestionで事前に処理済み）
        // コンテナ（アイコン+名前）は維持する
      } else {
        this._firstIntroShown = true
        if (chatQuestion) chatQuestion.classList.remove('ef-bubble-hidden')
      }
    } else if (chatQuestion) {
      chatQuestion.classList.remove('ef-bubble-hidden')
    }

    // tel質問のsub_messages[0]: 経過時間を動的に計算してテキストを差し替え
    if (questionId === 'tel') {
      const subMessages = questionElement.querySelectorAll('[data-ef-sub-message]')
      if (subMessages.length > 0) {
        const timeMsg = subMessages[0].querySelector('.ef-bubble-text, [data-ef-chat-question]') || subMessages[0]
        const elapsed = Date.now() - this._startTime
        const minutes = Math.floor(elapsed / (1000 * 60))
        const seconds = Math.floor((elapsed % (1000 * 60)) / 1000)
        if (minutes > 0 || seconds > 0) {
          const timeParts = []
          if (minutes > 0) timeParts.push(`${minutes}分`)
          if (seconds > 0) timeParts.push(`${seconds}秒`)
          timeMsg.textContent = `現在回答を始めてから${timeParts.join('')}が経過しましたが、残り1項目です。`
        }
      }
    }

    // プレースホルダーの置換（吹き出し表示時に再適用）
    // auto_continue チェーンで表示される質問のテキスト内 {{question_id}} を回答ラベルで置換
    this.applyAnswerPlaceholders(questionElement)

    // 補足説明を表示
    if (annotation) {
      this.fadeInElement(annotation)
    }

    // sub_messages_min_selections: show_ifの参照先質問の選択数が条件に満たない場合は非表示
    const questionData = this.questionsValue.find(q => q.id === questionId)
    const minSelections = questionData?.subMessagesMinSelections
    let showSubMessages = subMessages.length > 0
    if (showSubMessages && minSelections && questionData?.showIf) {
      const refQuestionId = questionData.showIf.question
      if (refQuestionId) {
        const answer = this.answers[refQuestionId]
        // value_any_ofが指定されている場合、該当する選択数のみカウント
        const valueAnyOf = questionData.showIf.value_any_of
        let selectedCount
        if (valueAnyOf && Array.isArray(answer)) {
          selectedCount = answer.filter(v => valueAnyOf.includes(v)).length
        } else {
          selectedCount = Array.isArray(answer) ? answer.length : (answer ? 1 : 0)
        }
        if (selectedCount < minSelections) {
          showSubMessages = false
          // 非表示のsub_message要素をDOMから隠す（空白防止）
          subMessages.forEach(msg => { msg.style.display = 'none' })
        }
      }
    }

    // 選択肢表示までの追加遅延（既存EFのdelayTime相当）
    const inputDelay = questionData2?.inputDelay || 0

    // 追加吹き出しメッセージがある場合は順番に表示してから入力エリアを表示
    if (showSubMessages) {
      // 既存EFに合わせたタイミング: 初回1000ms、2個目以降は+500ms追加（showNextChatSlow相当）
      const baseDelay = 1000 // 質問テキスト表示後の待ち時間
      const perMessage = 1000 // メッセージ間の基本間隔
      const extraDelay = 500 // 2個目以降の追加ウェイト
      let cumulativeDelay = baseDelay
      subMessages.forEach((msg, i) => {
        if (i > 0) cumulativeDelay += extraDelay
        const msgDelay = cumulativeDelay
        this._trackTimeout(() => {
          // sub_message内のプレースホルダーを表示前に置換
          const textSpan = msg.querySelector('.ef-bubble-text')
          if (textSpan && textSpan.textContent.includes('{{')) {
            textSpan.textContent = textSpan.textContent.replace(/\{\{(\w+)\}\}/g, (_, qId) => {
              return this.answerLabels[qId] || `{{${qId}}}`
            })
          }
          this.fadeInElement(msg)
        }, msgDelay)
        cumulativeDelay += perMessage
      })
      // すべてのsub_messages表示後に入力エリアを表示
      const totalDelay = cumulativeDelay + inputDelay
      this._trackTimeout(() => {
        // 最終パス: question要素内の全プレースホルダーを確実に置換
        questionElement.querySelectorAll('.ef-bubble-text').forEach(span => {
          if (span.textContent.includes('{{')) {
            span.textContent = span.textContent.replace(/\{\{(\w+)\}\}/g, (_, qId) => {
              return this.answerLabels[qId] || `{{${qId}}}`
            })
          }
        })
        // 選択肢を表示し、表示後にスクロール（選択肢の高さを含めた位置へ）
        if (chatInput) {
          this.fadeInElement(chatInput)
        }
        this.fadeInNavigationButton()
        this.scrollToShowFullQuestion(questionElement)
      }, totalDelay)
    } else {
      // sub_messagesがない場合
      // プレースホルダー置換（sub_messagesなしの質問でも必要）
      questionElement.querySelectorAll('.ef-bubble-text, [data-ef-chat-question]').forEach(span => {
        if (span.textContent.includes('{{')) {
          span.textContent = span.textContent.replace(/\{\{(\w+)\}\}/g, (_, qId) => {
            return this.answerLabels[qId] || `{{${qId}}}`
          })
        }
      })
      // 選択肢を表示し、表示後にスクロール（選択肢の高さを含めた位置へ）
      // 既存EFではテキスト表示からDELAY_TIME(800ms)後にchatInputを表示するため、同じ遅延を入れる
      const baseInputDelay = 800
      const totalInputDelay = inputDelay > 0 ? inputDelay : baseInputDelay
      this._trackTimeout(() => {
        if (chatInput) {
          this.fadeInElement(chatInput)
        }
        this.fadeInNavigationButton()
        this.scrollToShowFullQuestion(questionElement)
      }, totalInputDelay)
    }

    // 最終パス: transitionLoaderToQuestion完了後にプレースホルダーを確実に置換
    // （タイミング問題でapplyAnswerPlaceholdersの置換が効かないケースへの対処）
    this._trackTimeout(() => {
      questionElement.querySelectorAll('.ef-bubble-text, [data-ef-chat-question]').forEach(span => {
        if (span.textContent && span.textContent.includes('{{')) {
          span.textContent = span.textContent.replace(/\{\{(\w+)\}\}/g, (_, qId) => {
            return this.answerLabels[qId] || `{{${qId}}}`
          })
        }
      })
    }, 100)
  }

  // 入力エリア表示後のスクロール（本番と同じ方式）
  // 質問要素の上端へスクロールする（差分計算ではなく固定位置）
  // これにより、setupNextQuestionの初回スクロールと同じ位置に滑らかに移動する
  scrollToShowFullQuestion (questionElement) {
    this._trackTimeout(() => {
      window.scroll({ top: questionElement.offsetTop, left: 0, behavior: 'smooth' })
    }, 400)
  }

  // ナビゲーションボタン（グローバル「次へ進む」）のフェードイン
  // chatInput表示と同期して表示する（質問遷移時のフェードイン演出）
  fadeInNavigationButton () {
    if (!this.hasNextButtonTarget) return
    const navWrapper = this.nextButtonTarget.closest('[data-ef-renderer-target="navigation"]')
    if (navWrapper && this.nextButtonTarget.style.display !== 'none') {
      this.fadeInElement(navWrapper)
    }
  }

  // ef-hidden-until-shownの要素をフェードインで表示
  // ef-hidden-until-shownはvisibility:hidden!importantで非表示にしている
  // 除去すると、visibility-visibleクラスを持つ要素はCSS側のfade-in-visibilityアニメーションが発動する
  // visibility-visibleを持たない要素にはvisibility-visibleクラスを追加してフェードインを適用
  fadeInElement (element) {
    const visibilityClass = this.getVisibilityVisibleClass()
    if (!element.classList.contains(visibilityClass)) {
      element.classList.add(visibilityClass)
    }
    element.classList.remove('ef-hidden-until-shown')
  }

  // 前の回答に基づいて選択肢をフィルタリングまたはAjax取得
  filterOptionsBasedOnPreviousAnswer (questionElement) {
    const dependsOn = questionElement.dataset.dependsOn
    const ajaxChoices = questionElement.dataset.ajaxChoices === 'true'
    const ajaxEndpoint = questionElement.dataset.ajaxEndpoint

    // Ajax取得が必要な場合（choices_source + fetch: ajax）
    // 都道府県・市区町村などの動的選択肢はすべてこのパスを通る
    if (ajaxChoices && ajaxEndpoint && dependsOn) {
      const dependsOnValue = this.answers[dependsOn]
      if (dependsOnValue) {
        this.loadOptionsFromApi(questionElement, ajaxEndpoint, dependsOn, dependsOnValue)
      }
    }
  }

  // APIからオプションを取得して選択肢を更新（汎用版）
  async loadOptionsFromApi (questionElement, endpointTemplate, dependsOnKey, dependsOnValue) {
    // 前回のリクエストをキャンセル（戻る→進むの高速操作による競合防止）
    if (this._abortController) {
      this._abortController.abort()
    }
    this._abortController = new AbortController()

    try {
      // エンドポイントのプレースホルダーを置換
      // テンプレートは {depends_on} 形式（例: /area/regions/{depends_on}/prefectures.json）
      const endpoint = endpointTemplate.replace('{depends_on}', encodeURIComponent(dependsOnValue))
      const response = await fetch(endpoint, { signal: this._abortController.signal })
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`)
      }
      const options = await response.json()
      this.updateAjaxOptions(questionElement, options)
    } catch (error) {
      // AbortErrorはキャンセルによる正常な中断なので無視
      if (error.name === 'AbortError') return
      console.error('[EF Renderer] Failed to fetch options:', error)
      if (window.Sentry) window.Sentry.captureException(error)
      // 選択肢の取得失敗をユーザーに通知
      const optionsContainer = questionElement.querySelector('[data-ef-renderer-target="input"]')
      if (optionsContainer) {
        // 既存のエラーメッセージを削除してから新しいものを追加（蓄積防止）
        const existingError = optionsContainer.querySelector('.ef-ajax-error')
        if (existingError) existingError.remove()

        const errorMsg = document.createElement('p')
        errorMsg.className = 'ef-ajax-error'
        errorMsg.textContent = '選択肢の取得に失敗しました。ページを再読み込みしてください。'
        errorMsg.style.cssText = 'color: #dc3545; font-size: 14px; padding: 8px;'
        optionsContainer.appendChild(errorMsg)
      }
    }
  }

  // Ajax取得した選択肢を更新（汎用版）
  updateAjaxOptions (questionElement, options) {
    const optionsContainer = questionElement.querySelector('[data-ef-renderer-target="input"]')
    if (!optionsContainer) return

    const questionId = questionElement.dataset.questionId
    const questionData = this.questionsValue.find(q => q.id === questionId)
    const isCheckbox = questionData?.type === 'checkbox'
    const isListStyle = questionData?.displayStyle === 'list'

    // GTM連携用: Ajax取得の選択肢はvalueがDBのIDのため、YAML定義のoptions.ga_eventとはマッチしない
    // 質問レベルのga_eventのみ適用する（選択肢レベルは静的選択肢のみapplyGtmDataAttributesで対応）
    const gaEvent = questionData?.gaEvent
    const gtmAttributes = this.buildGtmAttributes(gaEvent)

    // 既存の選択肢をクリア（DOM APIで統一、innerHTMLは使用しない）
    optionsContainer.replaceChildren()

    if (isListStyle) {
      // 追加エリアの市区町村（show_if付き & モーダル以外）のみスクロール制限を追加
      // モーダル内リストはモーダル自体のサイズで制御するためスクロール制限不要
      const isModal = questionElement.querySelector('.ef-modal') != null || questionElement.closest('.ef-modal') != null
      const isAdditional = questionData?.showIf != null && !isModal
      if (isAdditional) {
        optionsContainer.classList.add('ef-cities--additional')
      } else {
        optionsContainer.classList.remove('ef-cities--additional')
      }

      // リスト形式: 選択済みテキスト表示用のdivがなければ追加
      if (!questionElement.querySelector('[data-ef-renderer-target="citiesSelectedText"]')) {
        const selectedTextDiv = document.createElement('div')
        selectedTextDiv.className = 'ef-cities-selected'
        selectedTextDiv.dataset.efRendererTarget = 'citiesSelectedText'
        optionsContainer.parentNode.insertBefore(selectedTextDiv, optionsContainer)
      }

      // 「他の希望エリア」の市区町村では、1回目に選択済みの市区町村をグレーアウト
      const selectedCityId = questionId === 'additional_city_id' ? this.answers.city_id : null

      // リスト形式（市区町村選択など）
      options.forEach(option => {
        const li = document.createElement('li')
        const label = document.createElement('label')
        const optionValue = option.id.toString()

        // 1回目に選択済みの市区町村はグレーアウト（既存EFと同じ挙動）
        if (selectedCityId && optionValue === selectedCityId.toString()) {
          label.dataset.status = 'selected'
        }

        const input = document.createElement('input')
        input.type = isCheckbox ? 'checkbox' : 'radio'
        input.name = isCheckbox ? `${questionId}[]` : questionId
        input.value = optionValue
        input.dataset.action = 'change->ef-renderer#onInputChange'
        this.setGtmAttributes(input, gtmAttributes)

        const span = document.createElement('span')
        span.textContent = option.name

        label.appendChild(input)
        label.appendChild(span)
        li.appendChild(label)
        optionsContainer.appendChild(li)
      })
    } else {
      // ボタン形式（デフォルト）
      const labelClass = this.findButtonClass()

      options.forEach(option => {
        const label = document.createElement('label')
        label.className = labelClass

        const input = document.createElement('input')
        input.type = isCheckbox ? 'checkbox' : 'radio'
        input.name = isCheckbox ? `${questionId}[]` : questionId
        input.value = option.id.toString()
        input.dataset.action = 'change->ef-renderer#onInputChange'
        this.setGtmAttributes(input, gtmAttributes)

        const span = document.createElement('span')
        span.textContent = option.name

        label.appendChild(input)
        label.appendChild(span)
        optionsContainer.appendChild(label)
      })
    }
  }

  // 本番チャットのボタンクラスを他の質問から探す
  findButtonClass () {
    // すべての質問からラベルを探す
    for (const question of this.questionTargets) {
      const label = question.querySelector('label')
      if (label && label.className) {
        return label.className
      }
    }
    return ''
  }

  // ナビゲーションの更新
  updateNavigation () {
    // 最後の質問かどうか
    const isLastQuestion = this.findNextVisibleQuestion(this.currentIndex) === null

    // 現在の質問のタイプを取得
    const currentQuestion = this.questionTargets[this.currentIndex]
    const questionId = currentQuestion?.dataset?.questionId
    const questionData = this.questionsValue.find(q => q.id === questionId)

    // 「次へ」ボタンが必要かどうかを判定
    // text、tel、email、textarea、priorityは「次へ」ボタンが必要
    // radio、selectは選択時に自動進行するので「次へ」ボタンは不要
    const needsNextButton = this.needsExplicitNextButton(questionData?.type)

    // checkbox, text_with_buttonはコンポーネント内に独自の「次へ進む」ボタンを持つ
    // confirm_button付きradioも同様
    // グローバル「次へ進む」ボタンとの重複を防ぐため、これらのタイプでは非表示
    const hasOwnNextButton = this.hasOwnNextButton(questionData?.type, questionData)

    if (this.hasNextButtonTarget) {
      const navWrapper = this.nextButtonTarget.closest('[data-ef-renderer-target="navigation"]')
      if (isLastQuestion || hasOwnNextButton || !needsNextButton) {
        this.nextButtonTarget.style.display = 'none'
        // ラッパーを非表示に戻す（次のフェードインに備える）
        if (navWrapper) navWrapper.classList.add('ef-hidden-until-shown')
      } else {
        this.nextButtonTarget.style.display = ''
      }
    }
    if (this.hasSubmitButtonTarget) {
      // 残りの表示可能な質問数をカウント（現在の質問を含む、message/level型はカウントしない）
      const remainingCount = this.countRemainingVisibleQuestions(this.currentIndex, true)
      const submitBtn = this.submitButtonTarget.querySelector('.ef-submit-button')
      // level/message型の質問では送信ボタンを表示しない
      const currentType = questionData?.type
      const hideForCurrentType = currentType === 'level' || currentType === 'message'

      if (remainingCount <= 2 && !hideForCurrentType) {
        // 残り2問以下でフッターを表示（level/message型は除く）
        this.submitButtonTarget.style.display = ''
        if (submitBtn) {
          // 残り項目数を表示（グレーアウト状態）
          submitBtn.textContent = `残り${remainingCount}項目です`
          submitBtn.disabled = true
        }
      } else {
        this.submitButtonTarget.style.display = 'none'
      }
    }

    // 次へ/送信ボタンの有効/無効状態を更新
    this.updateNextButtonState()
  }

  // 「次へ」ボタン（グローバル）を明示的に表示する必要がある質問タイプかどうか
  // checkbox, text_with_buttonはコンポーネント内に独自の「次へ進む」ボタンを持つため、グローバルボタンは不要
  needsExplicitNextButton (questionType) {
    // テキスト入力系は「次へ」ボタンが必要
    // checkbox: コンポーネント内にピンク色の「次へ進む」ボタンを配置
    // text_with_button: コンポーネント内に「次へ進む」ボタンを配置
    const typesNeedingNextButton = ['text', 'tel', 'email', 'textarea']
    return typesNeedingNextButton.includes(questionType)
  }

  // コンポーネント内に独自の「次へ進む」ボタンを持つ質問タイプかどうか
  // これらのタイプではグローバル「次へ進む」ボタンを非表示にする
  hasOwnNextButton (questionType, questionData) {
    const typesWithOwnNextButton = ['checkbox', 'text_with_button', 'priority', 'level']
    if (typesWithOwnNextButton.includes(questionType)) return true
    // confirm_button付きradioもコンポーネント内にボタンを持つ
    if (questionType === 'radio' && questionData?.confirmButton) return true
    return false
  }

  // 次へ/送信ボタンの有効/無効状態を更新
  updateNextButtonState () {
    const currentQuestion = this.questionTargets[this.currentIndex]
    const canProceed = this.canProceedFromQuestion(currentQuestion)

    // 次へボタン
    if (this.hasNextButtonTarget) {
      this.setButtonEnabled(this.nextButtonTarget, canProceed)
    }

    // 送信ボタン: 最後の質問で入力完了時のみ「問い合わせ（無料）」テキストに切り替え
    if (this.hasSubmitButtonTarget) {
      const submitBtn = this.submitButtonTarget.querySelector('.ef-submit-button')
      if (submitBtn) {
        const isLastQuestion = this.findNextVisibleQuestion(this.currentIndex) === null
        if (isLastQuestion && canProceed) {
          submitBtn.textContent = '問い合わせ（無料）'
          submitBtn.disabled = false
        } else if (isLastQuestion) {
          submitBtn.textContent = '残り1項目です'
          submitBtn.disabled = true
        } else {
          submitBtn.disabled = true
        }
      }
    }
  }

  // 現在の質問から次に進めるかどうかをチェック（バリデーションエラー表示なし）
  canProceedFromQuestion (questionElement) {
    const questionId = questionElement.dataset.questionId
    const questionData = this.questionsValue.find(q => q.id === questionId)

    // questionDataが見つからない、または必須でない場合は進める
    if (!questionData || !questionData.required) {
      return true
    }

    // message型は入力がないので常に進める
    if (questionData.type === 'message') {
      return true
    }

    const answer = this.answers[questionId]

    // 入力タイプに応じたチェック
    if (questionData.type === 'checkbox' || questionData.type === 'priority') {
      return Array.isArray(answer) && answer.length > 0
    } else if (questionData.type === 'tel') {
      const telPattern = /^[0-9]{10,11}$/
      const cleanedAnswer = answer ? answer.replace(/-/g, '') : ''
      return answer && answer !== '' && telPattern.test(cleanedAnswer)
    } else if (questionData.type === 'email') {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return answer && answer !== '' && emailPattern.test(answer)
    } else {
      // その他は値が存在すること
      return answer !== undefined && answer !== null && answer !== ''
    }
  }

  // 質問のバリデーション
  // 表示されている質問のうち、required=trueのものだけをバリデーション
  // canProceedFromQuestionでバリデーション判定し、NGの場合はエラーメッセージを生成
  validateQuestion (questionElement) {
    const isValid = this.canProceedFromQuestion(questionElement)

    // エラーメッセージの生成（バリデーションNG時のみ）
    // canProceedFromQuestionがfalseを返すのはquestionDataが存在しrequired=trueの場合のみ
    let errorMessage = ''
    if (!isValid) {
      const questionId = questionElement.dataset.questionId
      const questionData = this.questionsValue.find(q => q.id === questionId)
      if (!questionData) return isValid // 防御的ガード（通常到達しない）
      const answer = this.answers[questionId]

      // radio/select/level: 選択で自動遷移するためバリデーションエラーは表示しない
      // ユーザーが直接操作しないとgoNextが呼ばれない限り、エラーメッセージは不要
      if (['radio', 'select', 'level'].includes(questionData.type) && !questionData.confirmButton) {
        return isValid
      }

      if (questionData.type === 'checkbox' || questionData.type === 'priority') {
        errorMessage = '1つ以上選択してください'
      } else if (questionData.type === 'tel') {
        errorMessage = (!answer || answer === '') ? '電話番号を入力してください' : '電話番号は10〜11桁の数字で入力してください'
      } else if (questionData.type === 'email') {
        errorMessage = (!answer || answer === '') ? 'メールアドレスを入力してください' : '正しいメールアドレス形式で入力してください'
      } else {
        errorMessage = '入力してください'
      }
    }

    // エラー表示の更新
    this.showValidationError(questionElement, isValid, errorMessage)

    return isValid
  }

  // バリデーションエラーの表示/非表示
  showValidationError (questionElement, isValid, errorMessage) {
    // 入力要素のスタイル更新
    const inputs = questionElement.querySelectorAll('[data-ef-renderer-target="input"]')
    inputs.forEach(input => {
      if (!isValid) {
        input.classList.add('is-invalid')
      } else {
        input.classList.remove('is-invalid')
      }
    })

    // エラーメッセージ要素の取得または作成
    let errorEl = questionElement.querySelector('.ef-validation-error')
    if (!isValid && errorMessage) {
      if (!errorEl) {
        errorEl = document.createElement('div')
        errorEl.className = 'ef-validation-error'
        errorEl.style.cssText = 'color: #dc3545; font-size: 14px; margin-top: 8px; padding: 8px 12px; background: #fff5f5; border-radius: 4px;'
        questionElement.appendChild(errorEl)
      }
      errorEl.textContent = errorMessage
      errorEl.style.display = 'block'
    } else if (errorEl) {
      errorEl.style.display = 'none'
    }
  }

  // プレビュー送信処理（パラメータ確認後にリダイレクト先に遷移）
  submitPreview () {
    const currentQuestion = this.questionTargets[this.currentIndex]
    if (!this.validateQuestion(currentQuestion)) {
      return
    }

    // 送信時のGAイベントを送信（非クリティカル: 失敗しても送信処理は継続する）
    try {
      this.fireSubmitGaEvent()
    } catch (e) {
      console.error('[EF Renderer] Failed to fire submit GA event:', e)
      if (window.Sentry) window.Sentry.captureException(e)
    }

    // パラメータ確認モーダルを表示
    this.showSubmitParameters()
  }

  // 送信パラメータをモーダル表示
  showSubmitParameters () {
    const body = {
      ef_definition_id: this.definitionIdValue,
      ef_version: this.definitionVersionValue,
      answers: this.answers, // 内部処理用（value）
      answerLabels: this.answerLabels, // extra_input_params用（日本語ラベル）
    }

    const allowSubmission = this.allowSubmissionValue

    // 既存のモーダルがあれば削除
    const existingModal = document.getElementById('ef-preview-params-modal')
    if (existingModal) {
      existingModal.remove()
    }

    // モーダル作成
    const modal = document.createElement('div')
    modal.id = 'ef-preview-params-modal'
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `

    const content = document.createElement('div')
    content.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 700px;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    `

    const title = document.createElement('h3')
    title.textContent = '送信パラメータ（プレビュー）'
    title.style.cssText = 'margin: 0 0 16px 0; color: #333;'

    // 環境に応じた説明文（DOM APIで構築し、innerHTML/insertAdjacentHTMLは使わない）
    const description = document.createElement('p')
    description.style.cssText = 'margin: 0 0 16px 0; color: #666; font-size: 14px; line-height: 1.6;'

    if (allowSubmission) {
      const envLabel = document.createElement('strong')
      envLabel.style.color = '#28a745'
      envLabel.textContent = '開発/ステージング環境'
      description.appendChild(envLabel)
      description.appendChild(document.createTextNode('です。実際にフォームを送信できます。送信後はthanksページにリダイレクトされます。'))
    } else {
      description.appendChild(document.createTextNode('プレビューモードです。以下のパラメータがPOSTされます。'))
    }

    const pre = document.createElement('pre')
    pre.textContent = JSON.stringify(body, null, 2)
    pre.style.cssText = `
      background: #f5f5f5;
      padding: 16px;
      border-radius: 4px;
      overflow-x: auto;
      font-size: 12px;
      line-height: 1.5;
      margin: 0 0 16px 0;
      max-height: 300px;
    `

    // ボタンコンテナ
    const btnContainer = document.createElement('div')
    btnContainer.style.cssText = 'display: flex; gap: 12px;'

    const closeBtn = document.createElement('button')
    closeBtn.textContent = '閉じる'
    closeBtn.style.cssText = `
      background: #999;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      flex: 1;
    `
    closeBtn.addEventListener('click', () => modal.remove())

    btnContainer.appendChild(closeBtn)

    // 開発環境・ステージングでは実際に送信するボタンを追加
    if (allowSubmission) {
      const submitBtn = document.createElement('button')
      submitBtn.textContent = '実際に送信する'
      submitBtn.style.cssText = `
        background: #28a745;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        flex: 1;
        font-weight: bold;
      `
      submitBtn.addEventListener('click', async () => {
        submitBtn.disabled = true
        submitBtn.textContent = '送信中...'
        const success = await this.submitActualForm()
        if (success) {
          modal.remove()
        } else {
          submitBtn.disabled = false
          submitBtn.textContent = '実際に送信する'
        }
      })
      btnContainer.appendChild(submitBtn)
    }

    content.appendChild(title)
    content.appendChild(description)
    content.appendChild(pre)
    content.appendChild(btnContainer)
    modal.appendChild(content)

    // 背景クリックでも閉じる
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.remove()
    })

    document.body.appendChild(modal)
  }

  // ルート別の動的テキスト変更
  // 本番EFでは occupant_current_status_id の回答に応じてDOMを書き換えている
  // プレビューEFでも同じ挙動を再現する
  applyRouteSpecificText (questionEl) {
    const questionId = questionEl.dataset.questionId
    if (questionId !== 'occupant_current_status_home_id') return

    const status = this.answers.occupant_current_status_id
    if (!status) return

    // 質問テキスト要素を取得（チャット吹き出しのテキスト）
    const textEl = questionEl.querySelector('[data-ef-chat-question]')
    // 注記テキスト要素を取得（center-noteクラス）
    const footerEl = questionEl.querySelector('[class*="center-note"]')

    if (textEl) {
      if (['4', '5', '6'].includes(status)) {
        // 在宅・これから介護・その他: 現在形
        textEl.textContent = '入居希望者は一人暮らしですか？'
      } else if (status === '1') {
        // 病院: 入院以前
        textEl.textContent = '入居希望者は入院以前、一人暮らしでしたか？'
      } else {
        // 老健(2)・施設(3): 施設入居以前
        textEl.textContent = '入居希望者は施設入居以前、一人暮らしでしたか？'
      }
    }

    if (footerEl) {
      if (['4', '5', '6'].includes(status)) {
        // 在宅・これから介護・その他: 注釈非表示
        footerEl.style.display = 'none'
      } else if (status === '1') {
        // 病院: 退院
        footerEl.textContent = '※退院後は、入居までに日数を要する場合があり、一時帰宅を求められることがあります'
        footerEl.style.display = ''
      } else {
        // 老健(2)・施設(3): 退去
        footerEl.textContent = '※退去後は、入居までに日数を要する場合があり、一時帰宅を求められることがあります'
        footerEl.style.display = ''
      }
    }
  }

  // 設問テキスト内の {{question_id}} プレースホルダーを過去の回答ラベルで置換
  // 例: "入居希望者の住民票は{{prefecture_id}}{{city_id}}にありますか？"
  //   → "入居希望者の住民票は群馬県高崎市にありますか？"
  applyAnswerPlaceholders (questionElement) {
    // プレースホルダーを含む可能性のある全テキスト要素を収集
    const textElements = [
      questionElement.querySelector('[data-ef-chat-question]'),
      questionElement.querySelector('[class*="question-heading"]'),
      // sub_messages内のテキスト要素もプレースホルダー置換対象
      ...questionElement.querySelectorAll('[data-ef-sub-message] .ef-bubble-text'),
      ...questionElement.querySelectorAll('[data-ef-sub-message] [data-ef-chat-question]'),
    ].filter(Boolean)
    // 重複除去（同じ要素が複数セレクタにマッチする場合）
    const uniqueElements = [...new Set(textElements)]

    for (const textEl of uniqueElements) {
      if (!textEl || !textEl.dataset) continue

      // 初回呼び出し時にオリジナルHTMLを保存（<br>等のタグを維持）
      if (textEl.dataset.originalHtml === undefined) {
        textEl.dataset.originalHtml = textEl.innerHTML
      }

      const originalHtml = textEl.dataset.originalHtml
      if (!originalHtml) continue

      // {{question_id}} パターンをすべて検出して置換
      // answerLabelsに値がない場合はプレースホルダーをそのまま残す（後の呼び出しで置換される）
      const html = originalHtml.replace(/\{\{(\w+)\}\}/g, (_, questionId) => {
        const value = this.answerLabels[questionId]
        if (value === undefined || value === null || value === '') {
          return `{{${questionId}}}` // 未解決のプレースホルダーはそのまま残す
        }
        // HTMLエスケープして安全に挿入
        const temp = document.createElement('span')
        // 配列の場合は各項目を「」で囲んで「、」で連結（既存EFと同じ表示）
        if (Array.isArray(value)) {
          temp.textContent = value.map(v => `「${v}」`).join('、')
        } else {
          temp.textContent = value
        }
        return temp.innerHTML
      })
      textEl.innerHTML = html
    }
  }

  // 実際にフォームを送信（開発環境・ステージング用）
  async submitActualForm () {
    if (this.isSubmitting) return
    this.isSubmitting = true

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        throw new Error('CSRF token not found')
      }

      const body = {
        ef_definition_id: this.definitionIdValue,
        ef_version: this.definitionVersionValue,
        answers: this.answers,
        // answerLabelsは送信確認モーダル表示用。サーバー側では使用しない
        answerLabels: this.answerLabels,
      }

      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      })

      // レスポンスボディは一度しか読めないため、先にテキストとして取得
      const responseText = await response.text()

      if (!response.ok) {
        // サーバーがJSON形式のエラーを返した場合、そのメッセージをユーザーに表示
        // （セッション切れ、バリデーションエラー等の具体的なメッセージを伝える）
        let userMessage = `HTTP ${response.status}: ${responseText}`
        try {
          const errorJson = JSON.parse(responseText)
          if (errorJson.error) userMessage = errorJson.error
        } catch (_e) { /* JSONでなければそのまま */ }
        throw new Error(userMessage)
      }

      let result
      try {
        result = JSON.parse(responseText)
      } catch (parseError) {
        throw new Error(`サーバーからの応答を解析できませんでした (${parseError.message}): ${responseText.substring(0, 200)}`)
      }

      // 送信成功後のリダイレクト（サーバーからthanksページURLが返される）
      if (result.redirect_url) {
        // 相対パスまたは同一オリジンのURLのみ許可（オープンリダイレクト防止）
        if (result.redirect_url.startsWith('/')) {
          window.location.href = result.redirect_url
        } else {
          try {
            const url = new URL(result.redirect_url, window.location.origin)
            if (url.origin === window.location.origin) {
              window.location.href = result.redirect_url
            } else {
              console.warn('[EF Renderer] 外部リダイレクトをブロック:', result.redirect_url)
              alert('送信が完了しました。')
            }
          } catch {
            console.warn('[EF Renderer] 不正なリダイレクトURL:', result.redirect_url)
            alert('送信が完了しました。')
          }
        }
      } else {
        alert('送信が完了しました。')
      }
      return true
    } catch (error) {
      console.error('[EF Renderer] Submit failed:', error)
      if (window.Sentry) window.Sentry.captureException(error)
      this.showErrorMessage(error.message || '送信に失敗しました。再度お試しください。')
      return false
    } finally {
      this.isSubmitting = false
    }
  }

  // ボタンの有効/無効状態を切り替え（スタイルも含む）
  setButtonEnabled (button, enabled) {
    button.disabled = !enabled
    button.style.opacity = enabled ? '' : '0.5'
    button.style.cursor = enabled ? '' : 'not-allowed'
  }

  // エラーメッセージを表示
  showErrorMessage (message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.style.display = ''
    }
  }
}
